import { Link as RouterLink } from 'react-router-dom'
import { Text, Link } from '@asktia/tia-ui'
import { useQuery } from 'react-query'
import { BASE_API_URL, APPOINTMENT_PROFILE_NAMES } from 'src/globals'
import { AppointmentProfile } from 'src/types'
import { fetcher } from 'src/fetcher'
import { useUserInfo } from 'src/hooks'
import { getOfferingsParams } from 'src/utils'

export async function getAppointmentProfile(
    appointmentProfileUuid: string,
    appointmentSuggestionUuid?: string | null,
    patientId?: number
): Promise<AppointmentProfile | null> {
    const { MASSAGE, GROUP_THERAPY } = APPOINTMENT_PROFILE_NAMES

    const queryParams = new URLSearchParams()
    if (appointmentSuggestionUuid) {
        queryParams.set('appointmentSuggestionId', appointmentSuggestionUuid)
    }

    queryParams.set('patientId', patientId!.toString())

    const profile = await fetcher<AppointmentProfile>(
        `${BASE_API_URL}/schedule-api/api/v1/offerings/${appointmentProfileUuid}${getOfferingsParams(
            {
                patientId,
                appointmentSuggestionUuid:
                    appointmentSuggestionUuid ?? undefined
            }
        )}`
    )

    /**
     * This is a continued hack upon hack until we have time to do
     * https://asktia.atlassian.net/browse/PR-4090 and make all of these
     * fields data-driven.  This is intended to be a temporary solution.
     *
     * In this case, "acupuncture" is comprised of ~20 profiles.  Instead of
     * enumerating all of them, we just check if the label contains
     * "acupuncture" and then add the appropriate pricing details.
     */
    if (profile.label.toLowerCase().indexOf('acupuncture') > -1) {
        profile.pricingDetails = {
            text: (
                <>
                    We only accept insurance for acupuncture visits in
                    California. <br /> <br />
                    If you’re a Tia Essential member, please check your benefits
                    and if we are in network with your plan ahead of your visit.{' '}
                    <a
                        style={{
                            color: 'var(--theme-ui-colors-primaryButton)',
                            textDecoration: 'underline'
                        }}
                        href="http://asktia.com/acupuncture-benefits"
                        target="_blank"
                    >
                        Here's how.
                    </a>
                    <br />
                    <br />
                    Tia members should contact a Care Coordinator before their
                    appointment to confirm coverage.
                </>
            ),
            showServicePriceList: true
        }
    }

    // TODO TM-5952: we should move this into the DB
    switch (profile.name) {
        case 'therapy':
        case 'talk_therapy_intake':
        case 'talk_therapy_program':
        case 'talk_therapy_returning':
            profile.pricingDetails = {
                text: 'Your insurance may cover this service. Please contact a Care Coordinator before your appointment to check benefits.',
                showServicePriceList: true
            }
            profile.emergencyHelpText = (
                <Text
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 5,
                        mb: 6
                    }}
                >
                    If you're experiencing thoughts of suicide, have been
                    harmed, or are in need of urgent medical attention, please
                    call 911 or reach out to:
                    <br />
                    National Suicide Prevention Hotline:
                    <br />
                    <Link
                        sx={{ color: 'primaryButton' }}
                        href="tel:1-800-273-8255"
                    >
                        1-800-273-8255
                    </Link>
                    <br />
                    National Domestic Violence Hotline:
                    <br />
                    <Link
                        sx={{ color: 'primaryButton' }}
                        href="tel:1-800-799-SAFE"
                    >
                        1-800-799-SAFE (7233)
                    </Link>
                </Text>
            )
            break

        case 'cupping':
            profile.pricingDetails = {
                text: 'Tia is affordable and accessible with transparent pricing. We do not yet accept insurance for this service.',
                showServicePriceList: true
            }
            break

        case 'tia_whole_health_exam_mental_health':
            profile.pricingDetails = {
                text: 'While your annual exam is covered by insurance, the mental health package costs $70. During check-in, ask us for a receipt for HSA/FSA reimbursement!',
                showServicePriceList: true
            }
            profile.emergencyHelpText = (
                <Text
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 5,
                        mb: 6
                    }}
                >
                    If you're experiencing thoughts of suicide, have been
                    harmed, or are in need of urgent medical attention, please
                    call 911 or reach out to:
                    <br />
                    National Suicide Prevention Hotline:
                    <br />
                    <Link
                        sx={{ color: 'primaryButton' }}
                        href="tel:1-800-273-8255"
                    >
                        1-800-273-8255
                    </Link>
                    <br />
                    National Domestic Violence Hotline:
                    <br />
                    <Link
                        sx={{ color: 'primaryButton' }}
                        href="tel:1-800-799-SAFE"
                    >
                        1-800-799-SAFE (7233)
                    </Link>
                </Text>
            )
            break
        case 'pain':
            profile.emergencyHelpText = (
                <Text
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 5,
                        mb: 6
                    }}
                >
                    If you are experiencing severe or sudden pain or bleeding,
                    please seek emergency care immediately.
                </Text>
            )
            break
        case 'mental_health_medication_management':
            profile.emergencyHelpText = (
                <Text
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 5,
                        mb: 6
                    }}
                >
                    This is not a talk therapy appointment. If you are looking
                    to speak with one of our therapists, please book a talk
                    therapy appointment.
                </Text>
            )
            break
        case 'prescription_refill':
            profile.emergencyHelpText = (
                <Text
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 5,
                        mb: 6
                    }}
                >
                    Don’t forget to contact your Care Coordinator to see if you
                    have refills before booking!
                    <br />
                    If you need psychiatric medicine, please book a Mental
                    health medication management appointment.
                </Text>
            )

            profile.ancillaryDetails = (
                <Text
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 5,
                        mb: 6
                    }}
                >
                    <strong>Please note:</strong> Controlled medications (like
                    those prescribed for anxiety & pain) require a different
                    process. Please{' '}
                    <RouterLink
                        style={{
                            color: 'var(--theme-ui-colors-linkText)'
                        }}
                        to={
                            '/chat/new?&subject=Controlled Medication Prescription%20Refill&label=Prescription%20Refill'
                        }
                        title="Chat with a care team"
                    >
                        message your Care Team
                    </RouterLink>{' '}
                    if you need this type of refill.
                </Text>
            )

            break

        case 'tia_whole_health_exam':
            profile.pricingDetails = {
                text: (
                    <>
                        If you have in-network insurance with Tia, this
                        appointment will be billed as an office visit. Depending
                        on your plan, you might have a co-pay, deductible, or
                        other out-of-pocket costs. We recommend checking with
                        your insurance to confirm coverage before booking.
                    </>
                )
            }
            break
        case 'annual_preventative_exam':
            profile.pricingDetails = {
                text: (
                    <>
                        This appointment is most often covered 100% by
                        in-network insurance as long as you haven't already had
                        your annual or gynecological exam this year, but
                        co-pays, co-insurance, or deductibles may apply. Labs
                        and imaging may incur additional costs.
                    </>
                )
            }
            break

        case 'tia_whole_health_in_clinic_care':
            profile.pricingDetails = {
                text: (
                    <>
                        This appointment is covered by in-network insurance as
                        long as you haven’t already had your annual or
                        gynecological exam this year.*
                        <br />
                        <br />
                        *Extras like labs and imaging may incur additional
                        costs.
                    </>
                )
            }
            break

        case 'tia_whole_health_follow_up':
            profile.pricingDetails = {
                text: (
                    <>
                        We accept in-network{' '}
                        <Link
                            href="https://asktia.com/insurance"
                            target="_blank"
                        >
                            insurance
                        </Link>{' '}
                        for this appointment but you may be responsible for a
                        copay or coinsurance depending on your plan. We
                        recommend checking with your health plan before booking.
                    </>
                )
            }
            break
        case MASSAGE:
            profile.pricingDetails = {
                text: 'Tia is affordable and accessible with transparent pricing. We do not yet accept insurance for this service.',
                showServicePriceList: true
            }
            break
        case GROUP_THERAPY:
            profile.pricingDetails = {
                text: 'Your insurance may cover group therapy. Please contact a Care Coordinator before your appointment to check benefits.',
                showServicePriceList: true
            }
            break
        case 'pelvic_floor_physical_therapy_initial':
        case 'pelvic_floor_physical_therapy_returning':
            profile.pricingDetails = {
                text: 'Tia is affordable and accessible with transparent pricing. We do not yet accept insurance for this service.',
                showServicePriceList: true
            }
            break
        case 'acu_community':
            profile.pricingDetails = {
                text: '$65 for Community Acu (different than single session)',
                showServicePriceList: false
            }
            break
        case 'acu_allergies':
        case 'acu_mental_health':
        case 'acu_digestion':
        case 'acu_fatigue':
        case 'acu_headache':
        case 'acu_pain':
        case 'acu_sleep':
        case 'acu_fertility':
        case 'acu_pregnancy':
        case 'acu_postpartum':
        case 'acu_menopause':
        case 'acu_miscarriage':
        case 'acu_menstrual':
        case 'acu_general':
        case 'acu_cupping':
            profile.pricingDetails = {
                text: 'If you live in CA: your insurance may cover this service. Please contact a Care Coordinator before your appointment to check benefits.',
                showServicePriceList: true
            }
            break
        case 'coaching_focus_motivation':
        case 'coaching_boundaries':
        case 'coaching_general':
            profile.pricingDetails = {
                text: (
                    <>
                        Coaching is not covered by insurance. See our{' '}
                        <Link
                            href="https://asktia.com/tia-service-list"
                            target="_blank"
                        >
                            coaching prices
                        </Link>
                        .
                    </>
                ),
                // needs to be false if link in text
                showServicePriceList: false
            }
            break
        case 'mh_group_sexual_wellbeing':
        case 'mh_group_setting_boundaries':
        case 'mh_group_connecting_to_your_power':
        case 'mh_group_developing_body_image':
            profile.pricingDetails = {
                text: (
                    <>
                        Groups are not covered by insurance. See our{' '}
                        <Link
                            href="https://asktia.com/tia-service-list"
                            target="_blank"
                        >
                            groups prices
                        </Link>
                        .
                    </>
                ),
                // needs to be false if link in text
                showServicePriceList: false
            }
            break
        case 'cosderm_neuromodulator_30':
        case 'cosderm_neuromodulator_60':
        case 'cosderm_neuromodulator_adjustment':
        case 'cosderm_VIpeel':
        case 'cosderm_microneedling':
        case 'cosderm_cortisone_injection':
        case 'cosderm_IV_energy':
        case 'cosderm_IV_immunity':
        case 'cosderm_IV_myers':
        case 'cosderm_vitshot_energy':
        case 'cosderm_vitshot_glutathione':
        case 'cosderm_vitshot_NAD+':
            profile.pricingDetails = {
                text: (
                    <>
                        Cosmetic treatments are not covered by insurance.{' '}
                        <Link
                            href="https://asktia.com/tia-service-list"
                            target="_blank"
                        >
                            See our prices here.
                        </Link>
                    </>
                ),
                // needs to be false if link in text
                showServicePriceList: false
            }
            break
        case 'womens_longevity_assessment':
        case 'womens_longevity_followup':
            profile.pricingDetails = {
                text: 'At Tia, we have built an offering that addresses your current health history in the context of longevity to provide a high quality, and sometimes covered by insurance, visit. We will attempt to bill your insurance for this visit and if you have health concerns or medical history that puts you at risk for certain conditions, this visit may be covered. Otherwise, this visit will be billed as an out of pocket expense.',
                showServicePriceList: true
            }
            break
    }
    return profile
}

export const useAppointmentProfile = (
    appointmentProfileUuid?: string | null,
    appointmentSuggestionUuid?: string | null
) => {
    const { user, isLoading } = useUserInfo()
    const profileQuery = useQuery<AppointmentProfile | null>(
        [
            'appointment-profile',
            appointmentProfileUuid,
            appointmentSuggestionUuid,
            user?.id
        ],
        () =>
            getAppointmentProfile(
                appointmentProfileUuid!,
                appointmentSuggestionUuid,
                user?.id
            ),
        {
            staleTime: Infinity,
            enabled: !!appointmentProfileUuid && !!user?.id
        }
    )

    return {
        profile: profileQuery.data,
        isLoading: profileQuery.isLoading || isLoading
    }
}
